import { createAsyncThunk } from '@reduxjs/toolkit';
import { usersEndPoints } from 'api/users/users.constants';
import {
  TUsersLoginBE,
  TUsersLoginBody,
  TUsersRefreshBE,
  TUsersRegisterBE,
  TUsersRegisterBody,
  TUsersGetMeBE,
  TUsersGoogleAuthBE,
  TUsersGoogleAuthBody,
  TUsersVerifyEmailBE,
  TUsersVerifyEmailBody,
  TUsersRequestResetPasswordBE,
  TUsersRequestResetPasswordBody,
  TUsersResetPasswordBE,
  TUsersResetPasswordBody,
} from 'api/users/users.types';
import createHttpClient from 'store/httpClient';
import { TBEGenricResponse } from 'api/types';
import { setAccessToken, clearTokens } from '../auth/auth.slices';
import { CURRENT_USER_LOCAL_STORAGE_KEY } from './users.constants';
import { safelySetItem } from 'utils/localStorage';

// ---------------- REGISTER ----------------

export const registerUserAsyncThunk = createAsyncThunk<
  TUsersRegisterBE,
  { body: TUsersRegisterBody; onSuccess?: () => void }
>('users/register', async ({ body, onSuccess }, { rejectWithValue }) => {
  try {
    const httpClient = createHttpClient(undefined);

    const response = await httpClient.post<
      TBEGenricResponse<TUsersRegisterBE>,
      TUsersRegisterBody
    >(usersEndPoints.register(), body);

    if (onSuccess) {
      onSuccess();
    }

    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    return rejectWithValue(null);
  }
});

// ---------------- LOGIN ----------------
export const loginUserAsyncThunk = createAsyncThunk<
  TUsersLoginBE,
  {
    body: TUsersLoginBody;
    onSuccess?: () => void;
    onError?: () => void;
  }
>(
  'users/login',
  async ({ body, onSuccess, onError }, { dispatch, rejectWithValue }) => {
    try {
      const httpClient = createHttpClient(undefined);

      const response = await httpClient.post<
        TBEGenricResponse<TUsersLoginBE>,
        TUsersLoginBody
      >(usersEndPoints.login(), body);

      // Save accessToken to auth slice
      if (response.data?.accessToken) {
        dispatch(setAccessToken(response.data.accessToken));
      }

      if (onSuccess) {
        onSuccess();
      }

      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      if (onError) {
        onError();
      }
      return rejectWithValue(null);
    }
  }
);

// ---------------- REFRESH TOKEN ----------------
export const refreshTokenAsyncThunk = createAsyncThunk<
  TUsersRefreshBE,
  { isSilentLogin?: boolean; onError?: () => void } | void
>('users/refresh', async (args, { dispatch, rejectWithValue }) => {
  const isSilentLogin = typeof args === 'object' && args?.isSilentLogin;
  try {
    const httpClient = createHttpClient(undefined, isSilentLogin);

    const response = await httpClient.post<TBEGenricResponse<TUsersRefreshBE>>(
      usersEndPoints.refresh(),
      {} as Record<string, never>
    );

    // Save new accessToken to auth slice
    if (response.data?.accessToken) {
      dispatch(setAccessToken(response.data.accessToken));
    }

    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    console.log('error', args);
    if (typeof args === 'object' && args?.onError) {
      args.onError();
    }
    return rejectWithValue(null);
  }
});

// ---------------- LOGOUT ----------------
export const logoutUserAsyncThunk = createAsyncThunk<
  void,
  { onSuccess?: () => void } | void
>('users/logout', async (args, { dispatch, rejectWithValue }) => {
  try {
    const httpClient = createHttpClient(undefined);

    await httpClient.post(usersEndPoints.logout(), {} as Record<string, never>);

    // Clear tokens from auth slice
    dispatch(clearTokens());

    if (typeof args === 'object' && args?.onSuccess) {
      args.onSuccess();
    }

    return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    // Even if logout fails, clear tokens locally
    dispatch(clearTokens());
    if (typeof args === 'object' && args?.onSuccess) {
      args.onSuccess();
    }
    return rejectWithValue(null);
  }
});

// ---------------- GOOGLE AUTH ----------------
export const googleAuthAsyncThunk = createAsyncThunk<
  TUsersGoogleAuthBE,
  {
    body: TUsersGoogleAuthBody;
    onSuccess?: () => void;
    onError?: () => void;
  }
>(
  'users/googleAuth',
  async ({ body, onSuccess, onError }, { dispatch, rejectWithValue }) => {
    try {
      const httpClient = createHttpClient(undefined);

      const response = await httpClient.post<
        TBEGenricResponse<TUsersGoogleAuthBE>,
        TUsersGoogleAuthBody
      >(usersEndPoints.googleAuth(), body);

      // Save accessToken to auth slice
      if (response.data?.accessToken) {
        console.log('accessToken', response.data.accessToken);
        dispatch(setAccessToken(response.data.accessToken));
      }

      if (onSuccess) {
        onSuccess();
      }

      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      if (onError) {
        onError();
      }
      return rejectWithValue(null);
    }
  }
);

// ---------------- GET ME ----------------
export const getMeAsyncThunk = createAsyncThunk<TUsersGetMeBE, void>(
  'users/getMe',
  async (_, { rejectWithValue }) => {
    try {
      const httpClient = createHttpClient(undefined);

      const response = await httpClient.get<TBEGenricResponse<TUsersGetMeBE>>(
        usersEndPoints.getMe()
      );
      safelySetItem(CURRENT_USER_LOCAL_STORAGE_KEY, response.data);

      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      return rejectWithValue(null);
    }
  }
);

// ---------------- VERIFY EMAIL ----------------
export const verifyEmailAsyncThunk = createAsyncThunk<
  TUsersVerifyEmailBE,
  { token: TUsersVerifyEmailBody['token']; onSuccess?: () => void }
>('users/verifyEmail', async ({ token, onSuccess }, { rejectWithValue }) => {
  try {
    const httpClient = createHttpClient(undefined);

    const response = await httpClient.post<
      TBEGenricResponse<TUsersVerifyEmailBE>
    >(usersEndPoints.verifyEmail(), { token });

    if (onSuccess) {
      onSuccess();
    }

    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    return rejectWithValue(null);
  }
});

// ---------------- REQUEST RESET PASSWORD ----------------
export const requestResetPasswordAsyncThunk = createAsyncThunk<
  TUsersRequestResetPasswordBE,
  { email: TUsersRequestResetPasswordBody['email']; onSuccess?: () => void }
>(
  'users/requestResetPassword',
  async ({ email, onSuccess }, { rejectWithValue }) => {
    try {
      const httpClient = createHttpClient(undefined);

      const response = await httpClient.post<
        TBEGenricResponse<TUsersVerifyEmailBE>
      >(usersEndPoints.requestResetPassword(), { email });

      if (onSuccess) {
        onSuccess();
      }

      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      return rejectWithValue(null);
    }
  }
);

// ---------------- RESET PASSWORD ----------------
export const resetPasswordAsyncThunk = createAsyncThunk<
  TUsersResetPasswordBE,
  {
    token: TUsersResetPasswordBody['token'];
    password: TUsersResetPasswordBody['password'];
    onSuccess?: () => void;
  }
>(
  'users/resetPassword',
  async ({ token, password, onSuccess }, { rejectWithValue }) => {
    try {
      const httpClient = createHttpClient(undefined);

      const response = await httpClient.post<
        TBEGenricResponse<TUsersVerifyEmailBE>
      >(usersEndPoints.resetPassword(), { token, password });

      if (onSuccess) {
        onSuccess();
      }

      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      return rejectWithValue(null);
    }
  }
);
